/**
 * Servicio de Datos Mock
 * Monedero Digital NFC
 * 
 * Carga y gestiona datos mock desde archivos JSON
 */

import storageService from './storageService';
import usuariosData from '../data/usuarios.json';
import estudiantesData from '../data/estudiantes.json';
import productosData from '../data/productos.json';
import transaccionesData from '../data/transacciones.json';
import configuracionData from '../data/configuracion.json';

class MockDataService {
  /**
   * Inicializa los datos mock en localStorage si no existen
   */
  initializeMockData() {
    try {
      console.log('🔄 Inicializando datos mock...');

      // Solo inicializa si no hay datos previos
      if (!storageService.has('nfc_wallet_initialized')) {
        // Estudiantes
        if (!storageService.getStudents().length) {
          storageService.saveStudents(estudiantesData.estudiantes);
          console.log('✅ Estudiantes cargados:', estudiantesData.estudiantes.length);
        }

        // Productos
        if (!storageService.getProducts().length) {
          storageService.saveProducts(productosData.productos);
          console.log('✅ Productos cargados:', productosData.productos.length);
        }

        // Transacciones
        if (!storageService.getTransactions().length) {
          storageService.saveTransactions(transaccionesData.transacciones);
          console.log('✅ Transacciones cargadas:', transaccionesData.transacciones.length);
        }

        // Configuración
        if (!storageService.getConfig()) {
          storageService.saveConfig(configuracionData.configuracion);
          console.log('✅ Configuración cargada');
        }

        // Marca como inicializado
        storageService.set('nfc_wallet_initialized', true);
        console.log('✅ Datos mock inicializados correctamente');
      } else {
        console.log('ℹ️ Datos mock ya estaban inicializados');
      }

      return true;
    } catch (error) {
      console.error('❌ Error inicializando datos mock:', error);
      return false;
    }
  }

  /**
   * Reinicia todos los datos a los valores mock originales
   */
  resetToMockData() {
    try {
      console.log('🔄 Reiniciando a datos mock...');

      storageService.saveStudents(estudiantesData.estudiantes);
      storageService.saveProducts(productosData.productos);
      storageService.saveTransactions(transaccionesData.transacciones);
      storageService.saveConfig(configuracionData.configuracion);

      console.log('✅ Datos reiniciados correctamente');
      return true;
    } catch (error) {
      console.error('❌ Error reiniciando datos:', error);
      return false;
    }
  }

  /**
   * Obtiene todos los usuarios (para autenticación)
   */
  getUsers() {
    return usuariosData.usuarios;
  }

  /**
   * Obtiene un usuario por username
   */
  getUserByUsername(username) {
    return usuariosData.usuarios.find(u => u.username === username);
  }

  /**
   * Obtiene la configuración de la aplicación
   */
  getConfig() {
    return storageService.getConfig() || configuracionData.configuracion;
  }

  /**
   * Obtiene todos los estudiantes
   */
  getStudents() {
    return storageService.getStudents();
  }

  /**
   * Obtiene un estudiante por ID
   */
  getStudentById(id) {
    const students = this.getStudents();
    return students.find(s => s.id === id);
  }

  /**
   * Obtiene un estudiante por NFC ID
   */
  getStudentByNFC(nfcId) {
    return storageService.findStudentByNFC(nfcId);
  }

  /**
   * Actualiza un estudiante
   */
  updateStudent(studentId, updates) {
    const students = this.getStudents();
    const index = students.findIndex(s => s.id === studentId);
    
    if (index !== -1) {
      students[index] = { ...students[index], ...updates };
      storageService.saveStudents(students);
      return students[index];
    }
    
    return null;
  }

  /**
   * Obtiene todos los productos
   */
  getProducts() {
    return storageService.getProducts();
  }

  /**
   * Obtiene productos de un vendedor específico
   */
  getProductsByVendor(vendorId) {
    const products = this.getProducts();
    return products.filter(p => p.vendedorId === vendorId);
  }

  /**
   * Obtiene un producto por ID
   */
  getProductById(id) {
    const products = this.getProducts();
    return products.find(p => p.id === id);
  }

  /**
   * Crea un nuevo producto
   */
  createProduct(product) {
    const products = this.getProducts();
    products.push(product);
    storageService.saveProducts(products);
    return product;
  }

  /**
   * Actualiza un producto
   */
  updateProduct(productId, updates) {
    const products = this.getProducts();
    const index = products.findIndex(p => p.id === productId);
    
    if (index !== -1) {
      products[index] = { ...products[index], ...updates };
      storageService.saveProducts(products);
      return products[index];
    }
    
    return null;
  }

  /**
   * Elimina un producto
   */
  deleteProduct(productId) {
    const products = this.getProducts();
    const filtered = products.filter(p => p.id !== productId);
    
    if (filtered.length < products.length) {
      storageService.saveProducts(filtered);
      return true;
    }
    
    return false;
  }

  /**
   * Obtiene todas las transacciones
   */
  getTransactions() {
    return storageService.getTransactions();
  }

  /**
   * Obtiene transacciones de un vendedor
   */
  getTransactionsByVendor(vendorId) {
    const transactions = this.getTransactions();
    return transactions.filter(t => t.vendedorId === vendorId);
  }

  /**
   * Obtiene transacciones de un estudiante
   */
  getTransactionsByStudent(studentId) {
    const transactions = this.getTransactions();
    return transactions.filter(t => t.estudianteId === studentId);
  }

  /**
   * Crea una nueva transacción
   */
  createTransaction(transaction) {
    storageService.addTransaction(transaction);
    return transaction;
  }

  /**
   * Obtiene estadísticas básicas
   */
  getStatistics(vendorId = null) {
    const transactions = vendorId 
      ? this.getTransactionsByVendor(vendorId)
      : this.getTransactions();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayTransactions = transactions.filter(t => {
      const txDate = new Date(t.timestamp);
      txDate.setHours(0, 0, 0, 0);
      return txDate.getTime() === today.getTime();
    });

    const todayPayments = todayTransactions.filter(t => t.tipo === 'pago');
    const todayRecharges = todayTransactions.filter(t => t.tipo === 'recarga');

    const totalVentas = todayPayments.reduce((sum, t) => sum + Math.abs(t.monto), 0);
    const totalRecargas = todayRecharges.reduce((sum, t) => sum + t.monto, 0);

    return {
      totalTransactionsToday: todayTransactions.length,
      totalPaymentsToday: todayPayments.length,
      totalRechargesToday: todayRecharges.length,
      totalVentasToday: totalVentas,
      totalRecargasToday: totalRecargas,
      allTimeTransactions: transactions.length
    };
  }
}

// Exporta una instancia única (Singleton)
const mockDataService = new MockDataService();
export default mockDataService;
